import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiGet, apiPatch, apiPost, contactEndpoints, operationsClient } from '@/lib/api';
import { contactKeys, membershipKeys } from '@/lib/queryKeys';
import type {
  ContactMembershipBillingHistoryResponse,
  ContactMembershipOpportunity,
  ContactMembershipPaymentMethodSummary,
  ContactPaginatedResponse,
} from '@/types/contact';
import type { Membership } from '@/types/membership';
import { membershipPlanKeys } from '@/lib/queryKeys';
import type { MembershipPlan } from '@/components/organisms/pricebook/support/membership-plan-shared';

export interface ContactActiveMembership extends Membership {
  paymentMethod?: ContactMembershipPaymentMethodSummary | null;
}

export interface CreateContactMembershipPayload {
  planId: string;
  paymentMethodId?: string;
  contractType?: 'MONTH_TO_MONTH' | 'ANNUAL_CONTRACT' | 'CUSTOM_TERM';
  contractTermMonths?: number;
  autoRenew?: boolean;
  notes?: string;
}

export interface UpdateContactMembershipPayload {
  membershipId: string;
  planId?: string;
  paymentMethodId?: string;
  autoRenew?: boolean;
  notes?: string;
}

export interface CancelContactMembershipPayload {
  membershipId: string;
  mode: 'IMMEDIATE' | 'AT_PERIOD_END';
  reason?: string;
  reasonCategory?:
    | 'PRICE'
    | 'NOT_USING'
    | 'COMPETITOR'
    | 'MOVING'
    | 'BUSINESS_CLOSED'
    | 'PAYMENT_ISSUES'
    | 'SERVICE_QUALITY'
    | 'OTHER';
}

function normalizePaginated<T>(raw: Record<string, unknown>): ContactPaginatedResponse<T> {
  const items = (raw.data ?? []) as T[];
  return {
    data: items,
    total: Number(raw.total ?? items.length),
    page: Number(raw.page ?? 1),
    limit: Number(raw.limit ?? items.length ?? 20),
    totalPages: Number(raw.totalPages ?? 1),
    hasMore: Boolean(raw.hasMore ?? false),
  };
}

function invalidateContactMembershipQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  contactId: string,
) {
  void queryClient.invalidateQueries({ queryKey: contactKeys.detail(contactId) });
  void queryClient.invalidateQueries({ queryKey: contactKeys.summary(contactId) });
  void queryClient.invalidateQueries({ queryKey: contactKeys.membershipsBase(contactId) });
  void queryClient.invalidateQueries({ queryKey: contactKeys.paymentMethods(contactId) });
  void queryClient.invalidateQueries({ queryKey: membershipKeys.all() });
}

export function useContactMemberships(contactId: string, params: Record<string, unknown> = {}) {
  return useApiQuery<ContactPaginatedResponse<Membership>>({
    queryKey: contactKeys.memberships(contactId, params),
    enabled: Boolean(contactId),
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>>(
        operationsClient,
        contactEndpoints.memberships(contactId),
        { params },
      );
      return normalizePaginated<Membership>(raw);
    },
    showErrorToast: false,
  });
}

export function useContactActiveMembership(contactId: string) {
  return useApiQuery<{ data: ContactActiveMembership | null }>({
    queryKey: contactKeys.membershipActive(contactId),
    enabled: Boolean(contactId),
    queryFn: () =>
      apiGet<{ data: ContactActiveMembership | null }>(
        operationsClient,
        contactEndpoints.activeMembership(contactId),
      ),
    showErrorToast: false,
  });
}

export function useContactMembershipBillingHistory(
  contactId: string,
  membershipId: string | null | undefined,
  params: Record<string, unknown> = {},
  enabled = true,
) {
  return useApiQuery<ContactMembershipBillingHistoryResponse>({
    queryKey: contactKeys.membershipBillingHistory(contactId, membershipId ?? '__none__', params),
    enabled: Boolean(contactId && membershipId && enabled),
    queryFn: () =>
      apiGet<ContactMembershipBillingHistoryResponse>(
        operationsClient,
        contactEndpoints.membershipBillingHistory(contactId, membershipId!),
        { params },
      ),
    showErrorToast: false,
  });
}

export function useContactMembershipOpportunity(contactId: string) {
  return useApiQuery<{ data: ContactMembershipOpportunity | null }>({
    queryKey: contactKeys.membershipOpportunity(contactId),
    enabled: Boolean(contactId),
    queryFn: () =>
      apiGet<{ data: ContactMembershipOpportunity | null }>(
        operationsClient,
        contactEndpoints.membershipOpportunity(contactId),
      ),
    showErrorToast: false,
  });
}

export function useActiveMembershipPlans() {
  const params = { status: 'ACTIVE', limit: 100 };

  return useApiQuery<ContactPaginatedResponse<MembershipPlan>>({
    queryKey: membershipPlanKeys.list(params),
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>>(
        operationsClient,
        '/pricebook/membership-plans',
        { params },
      );
      return normalizePaginated<MembershipPlan>(raw);
    },
    showErrorToast: false,
  });
}

export function useCreateContactMembership(contactId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<{ data: Membership }, CreateContactMembershipPayload>({
    mutationFn: (dto) =>
      apiPost<{ data: Membership }>(operationsClient, contactEndpoints.memberships(contactId), dto),
    onSuccess: () => {
      invalidateContactMembershipQueries(queryClient, contactId);
      toast.success('Membership added');
    },
  });
}

export function useUpdateContactMembership(contactId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<{ data: Membership }, UpdateContactMembershipPayload>({
    mutationFn: ({ membershipId, ...dto }) =>
      apiPatch<{ data: Membership }>(
        operationsClient,
        contactEndpoints.membership(contactId, membershipId),
        dto,
      ),
    onSuccess: () => {
      invalidateContactMembershipQueries(queryClient, contactId);
      toast.success('Membership updated');
    },
  });
}

export function useCancelContactMembership(contactId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<{ data: Membership }, CancelContactMembershipPayload>({
    mutationFn: ({ membershipId, ...dto }) =>
      apiPost<{ data: Membership }>(
        operationsClient,
        `${contactEndpoints.membership(contactId, membershipId)}/cancel`,
        dto,
      ),
    onSuccess: () => {
      invalidateContactMembershipQueries(queryClient, contactId);
      toast.success('Membership cancelled');
    },
  });
}
